/**
 * Pipeline Report Sync dialog (Reporting → Pipeline Report tab).
 *
 * Pulls the full gateway Deal feed and reconciles it into `pipeline_report_entry` — the
 * live counterpart to the CSV/XLSX importer (see PipelineReportImportDialog.tsx, whose
 * trigger button this dialog replaces; that file is left untouched, just unmounted).
 *
 * Step 1 ("start"): a single "Preview Sync" button — POSTs commit=false to get a dry-run
 * diff. Nothing is written yet.
 * Step 2 ("review"): the preview's per-Deal outcomes, grouped by status (Added / Updated /
 * Hidden / Reactivated / Unchanged) so the rows that actually change are easy to scan.
 * "Confirm Sync" re-POSTs with commit=true.
 * Step 3 ("done"): a brief success summary, then the table refetches on close.
 *
 * Unlike the importer this never creates a row from a file the user controls — every Deal
 * comes straight from the gateway, so there's no file picker and no "unmatched" concept.
 * A Deal can, however, cause a new local Account to be created (`accountsCreated`) or be
 * skipped outright for not being `IsPipeline` (`skippedNonPipeline`) — both surfaced above
 * the row list when non-zero.
 */

import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type {
  PipelineDealSyncResult,
  PipelineDealSyncRowResult,
  PipelineDealSyncRowStatus,
} from "@/hooks/use-pipeline-report";
import { useSyncPipelineDeals } from "@/hooks/use-pipeline-report";
import { formatDateMDY } from "@/lib/format";
import { formatPlanMoney } from "@/lib/plan-format";
import { pipelineReportKeys } from "@/lib/query";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type Step = "start" | "review" | "done";

const STATUS_LABELS: Record<PipelineDealSyncRowStatus, string> = {
  added: "Added",
  updated: "Updated",
  hidden: "Hidden",
  reactivated: "Reactivated",
  unchanged: "Unchanged",
};

/** Display order for both the summary counts and the grouped row list. */
const STATUS_ORDER: PipelineDealSyncRowStatus[] = [
  "added",
  "updated",
  "hidden",
  "reactivated",
  "unchanged",
];

const FIELD_LABELS: Record<string, string> = {
  clientAccountId: "Account",
  deal: "Deal",
  stage: "Stage",
  total: "Total",
  commitDate: "Commit",
  leadSource: "Lead Source",
};

/** Renders one side of a change's from/to pair. Sync-diff fields are all plain gateway
 * values (no ids to resolve to a display name — contrast PipelineReportImportDialog's
 * `nameById`, which exists only because that importer diffs RM-editable lookup fields). */
function formatChangeValue(field: string, value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (field === "total" && typeof value === "string") return formatPlanMoney(value);
  if (field === "commitDate" && typeof value === "string") return formatDateMDY(value);
  return typeof value === "string" ? value : String(value);
}

/** "5 added, 12 updated, 3 hidden" — omits zero counts, empty string when everything's zero. */
function summarizeStatusCounts(rows: PipelineDealSyncRowResult[]): string {
  const counts: Record<PipelineDealSyncRowStatus, number> = {
    added: 0,
    updated: 0,
    hidden: 0,
    reactivated: 0,
    unchanged: 0,
  };
  for (const r of rows) counts[r.status]++;
  return STATUS_ORDER.filter((s) => counts[s] > 0)
    .map((s) => `${counts[s]} ${STATUS_LABELS[s].toLowerCase()}`)
    .join(", ");
}

function groupByStatus(
  rows: PipelineDealSyncRowResult[],
): Record<PipelineDealSyncRowStatus, PipelineDealSyncRowResult[]> {
  const groups: Record<PipelineDealSyncRowStatus, PipelineDealSyncRowResult[]> = {
    added: [],
    updated: [],
    hidden: [],
    reactivated: [],
    unchanged: [],
  };
  for (const r of rows) groups[r.status].push(r);
  return groups;
}

function SyncRowItem({ row }: { row: PipelineDealSyncRowResult }) {
  const changes = row.changes ?? [];
  return (
    <li className="rounded-md border border-border px-3 py-2 text-sm">
      <p className="font-medium">
        {row.account} · {row.deal}
      </p>
      {changes.length > 0 && (
        <ul className="mt-1.5 space-y-1">
          {changes.map((c) => (
            <li key={c.field} className="flex flex-wrap items-baseline gap-1.5 text-xs">
              <span className="font-medium text-muted-foreground">
                {FIELD_LABELS[c.field] ?? c.field}:
              </span>
              <span className="text-muted-foreground line-through">
                {formatChangeValue(c.field, c.from)}
              </span>
              <span className="text-muted-foreground">→</span>
              <span className="font-medium text-foreground">
                {formatChangeValue(c.field, c.to)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

function SyncRowGroup({ title, rows }: { title: string; rows: PipelineDealSyncRowResult[] }) {
  if (rows.length === 0) return null;
  return (
    <section>
      <h3 className="mb-1.5 px-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {title} ({rows.length})
      </h3>
      <ul className="space-y-1.5">
        {rows.map((row) => (
          <SyncRowItem key={row.dealId} row={row} />
        ))}
      </ul>
    </section>
  );
}

export function PipelineReportSyncDialog({ open, onOpenChange }: Props) {
  const [step, setStep] = useState<Step>("start");
  const [preview, setPreview] = useState<PipelineDealSyncResult | null>(null);
  const [result, setResult] = useState<PipelineDealSyncResult | null>(null);

  const sync = useSyncPipelineDeals();
  const qc = useQueryClient();

  const grouped = useMemo(() => groupByStatus(preview?.rows ?? []), [preview]);

  function reset() {
    setStep("start");
    setPreview(null);
    setResult(null);
  }

  function handleOpenChange(next: boolean) {
    if (!next) reset();
    onOpenChange(next);
  }

  function handlePreview() {
    sync.mutate(
      { commit: false },
      {
        onSuccess: (res) => {
          setPreview(res);
          setStep("review");
        },
      },
    );
  }

  function handleBack() {
    setPreview(null);
    setStep("start");
  }

  function handleConfirm() {
    sync.mutate(
      { commit: true },
      {
        onSuccess: (res) => {
          setResult(res);
          setStep("done");
          qc.invalidateQueries({ queryKey: pipelineReportKeys.all });
          const summary = summarizeStatusCounts(res.rows);
          toast.success(
            res.rows.length > 0 ? `Pipeline Report synced — ${summary}.` : "Sync complete.",
          );
        },
      },
    );
  }

  const pending = sync.isPending;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl">
        {step === "start" && (
          <>
            <DialogHeader>
              <DialogTitle>Sync Pipeline Report</DialogTitle>
              <DialogDescription>
                Pull the latest Deals from the gateway and preview how the Pipeline Report would
                change. Nothing is saved until you confirm.
              </DialogDescription>
            </DialogHeader>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={pending}
              >
                Cancel
              </Button>
              <Button type="button" onClick={handlePreview} disabled={pending}>
                {pending ? "Previewing…" : "Preview Sync"}
              </Button>
            </DialogFooter>
          </>
        )}

        {step === "review" && preview && (
          <>
            <DialogHeader>
              <DialogTitle>Review Sync</DialogTitle>
              <DialogDescription>
                {preview.rows.length} deal{preview.rows.length === 1 ? "" : "s"} synced
                {preview.rows.length > 0 ? ` — ${summarizeStatusCounts(preview.rows)}` : ""}.
                Nothing has been saved yet.
              </DialogDescription>
            </DialogHeader>

            {(preview.accountsCreated > 0 || preview.skippedNonPipeline > 0) && (
              <div className="space-y-1 rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                {preview.accountsCreated > 0 && (
                  <p>
                    {preview.accountsCreated} new Account
                    {preview.accountsCreated === 1 ? "" : "s"} will be created.
                  </p>
                )}
                {preview.skippedNonPipeline > 0 && (
                  <p>
                    {preview.skippedNonPipeline} non-pipeline deal
                    {preview.skippedNonPipeline === 1 ? "" : "s"} skipped.
                  </p>
                )}
              </div>
            )}

            <div className="max-h-[420px] space-y-4 overflow-y-auto rounded-md border border-border p-2">
              {preview.rows.length === 0 ? (
                <p className="px-1 py-2 text-sm text-muted-foreground">No Deals found.</p>
              ) : (
                <>
                  <SyncRowGroup title="Added" rows={grouped.added} />
                  <SyncRowGroup title="Updated" rows={grouped.updated} />
                  <SyncRowGroup title="Hidden" rows={grouped.hidden} />
                  <SyncRowGroup title="Reactivated" rows={grouped.reactivated} />
                  <SyncRowGroup title="Unchanged" rows={grouped.unchanged} />
                </>
              )}
            </div>

            <DialogFooter className="sm:justify-between">
              <Button type="button" variant="outline" onClick={handleBack} disabled={pending}>
                Back
              </Button>
              <Button
                type="button"
                onClick={handleConfirm}
                disabled={pending || preview.rows.length === 0}
              >
                {pending ? "Syncing…" : "Confirm Sync"}
              </Button>
            </DialogFooter>
          </>
        )}

        {step === "done" && result && (
          <>
            <DialogHeader>
              <DialogTitle>Sync Complete</DialogTitle>
              <DialogDescription>
                {result.rows.length} deal{result.rows.length === 1 ? "" : "s"} synced
                {result.rows.length > 0 ? ` — ${summarizeStatusCounts(result.rows)}` : ""}.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button type="button" onClick={() => handleOpenChange(false)}>
                Done
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
